import React from 'react'
import { useRouter } from 'next/router'
import NextLink from 'next/link'

import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Spinner } from '@wartek-id/spinner'

import { Header } from 'app/KinerjaShared/components/Header'
import { SimpleCard } from 'app/KinerjaPSPegawaiPage/components/SimpleCard'
import { DialogMulaiPelaksanaanConfirmation } from 'app/KinerjaPSPegawaiPage/components/DialogMulaiPelaksanaanConfirmation'
import PlanningCard from 'app/KinerjaPSPegawaiPage/pages/Perencanaan/Beranda/PlanningCard'
import { PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import {
  usePerencanaanPSSummary,
  usePSStatus,
} from 'app/KinerjaPSPegawaiPage/hooks/useSummary'
import {
  usePerilakuKerjaStore,
  usePraktikKinerjaDraftStore,
  usePengembanganKompetensiDraftStore,
} from 'app/KinerjaPSPegawaiPage/stores/kinerja-ps'
import { useStoreByUserId } from 'app/KinerjaPSPegawaiPage/hooks/useStoreByUserId'
import { DialogPelaksanaanTugas } from 'app/KinerjaPSPegawaiPage/components/DialogPelaksanaanTugas'
import { PANDUAN_TAHAPAN_PERENCANAAN_KINERJA_URL } from 'app/KinerjaPSPegawaiPage/constants/panduan'
import { usePPSTrackEvent } from 'app/KinerjaPSPegawaiPage/hooks/usePPSTrackEvent'
import useToggleSdkContext from 'utils/useToogleSdk'
import TugasPokokCTA from 'app/KinerjaPSPegawaiPage/pages/Perencanaan/TugasPokok/TugasPokokCTA'

export function PSPerencanaanBerandaPage() {
  const [openDialog, setOpenDialog] = React.useState<
    'mulai-pelaksanaan' | 'pelaksanaan-tugas' | null
  >(null)
  const router = useRouter()
  const toggle = useToggleSdkContext()
  const pelaksanaanAutoStartEnabled =
    toggle?.['kinerja-ps-pegawai-pelaksanaan-auto-start-enabled'] || false

  let ctaDescription = 'Lengkapi Perencanaan sebelum ajukan'

  // set Store by User ID
  useStoreByUserId()

  const { trackEvent } = usePPSTrackEvent()

  const { perencanaan, apiStatus, isPerencanaanV2 } = usePerencanaanPSSummary()
  const { summary, summaryFetchStatus, isPelaksanaanState } = usePSStatus()
  const { hasDraft: hasPraktikKinerjaDraft } = usePraktikKinerjaDraftStore(
    (state) => ({
      hasDraft: state.hasDraft,
    })
  )
  const { hasDraft: hasPerilakuKerjaDraft } = usePerilakuKerjaStore(
    (state) => ({
      hasDraft: state.hasDraft,
    })
  )
  const { hasDraft: hasPengembanganKompetensiDraft } =
    usePengembanganKompetensiDraftStore((state) => ({
      hasDraft: state.hasDraft,
    }))

  const isAllReady = summary?.perencanaanStatus === 'SIAP_DIAJUKAN'
  const isAllWaitingForApproval =
    summary?.perencanaanStatus === 'SEDANG_DIPERIKSA' ||
    summary?.perencanaanStatus === 'DIKIRIM_TK' ||
    summary?.perencanaanStatus === 'SUDAH_DIREVISI'
  const isAllApproved = ['DISETUJUI_PPK', 'PELAKSANAAN_DIMULAI'].includes(
    summary?.perencanaanStatus
  )
  const isNeedRevisionByTK =
    summary?.perencanaanStatus === 'DIARAHKAN_REVISI_TK'
  const isNeedRevisionByPPK =
    summary?.perencanaanStatus === 'DIARAHKAN_REVISI_PPK'
  const isNeedRevision = isNeedRevisionByTK || isNeedRevisionByPPK
  const isRangkumanEnabled = isAllWaitingForApproval || isAllApproved

  if (isAllReady) {
    ctaDescription = 'Anda dapat ajukan sekarang'
  } else if (isAllWaitingForApproval) {
    ctaDescription =
      'Perencanaan sedang diperiksa oleh Tim Kinerja dan Pejabat Penilai Kinerja'
  }

  const handleBack = () => {
    router.push(PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH)
  }

  React.useEffect(() => {
    if (isPelaksanaanState && pelaksanaanAutoStartEnabled) {
      router.replace(PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPelaksanaanState, pelaksanaanAutoStartEnabled])

  if (
    summaryFetchStatus === 'loading' ||
    apiStatus === 'loading' ||
    isPelaksanaanState
  ) {
    return (
      <div className="flex justify-center w-full pt-16">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="w-full">
      <div className="p-8 bg-white">
        <Header
          title="Perencanaan Kinerja"
          onBack={handleBack}
          rightNode={
            <div className="flex gap-4 items-center w-[334px]">
              {!(isAllApproved || isNeedRevision) && (
                <Text
                  data-testid="cta-description"
                  variant="body-sm"
                  color="subdued"
                  className="text-right max-w-[256px]"
                >
                  {ctaDescription}
                </Text>
              )}
              {!isAllWaitingForApproval && !isAllApproved && (
                <Button
                  size="lg"
                  className="ml-auto"
                  disabled={!(isAllReady || isNeedRevision)}
                  onClick={() => {
                    router.push(
                      `${PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH}/perencanaan/ajukan`
                    )
                  }}
                >
                  Ajukan
                </Button>
              )}
              {isAllApproved && !pelaksanaanAutoStartEnabled && (
                <Button
                  size="lg"
                  className="ml-auto"
                  onClick={() => {
                    setOpenDialog('mulai-pelaksanaan')
                  }}
                >
                  Mulai Pelaksanaan
                </Button>
              )}
            </div>
          }
        />
      </div>

      <div className="flex justify-center gap-5 mt-10">
        <SimpleCard className="p-5 flex flex-col gap-6 max-w-[344px] h-fit">
          <Text variant="heading-sm">Tahapan Perencanaan Kinerja</Text>

          <ol className="flex flex-col gap-4 ml-5 list-decimal">
            <Text as="li">Mulai dan ajukan Perencanaan</Text>
            <Text as="li">
              Pemeriksaan oleh Tim Kinerja dan Pejabat Penilai Kinerja
              <Text
                as="span"
                variant="body-sm"
                color="subdued"
                className="block mt-1"
              >
                Lakukan revisi jika ada arahan
              </Text>
            </Text>
            <Text as="li">
              Persetujuan Perencanaan oleh Pejabat Penilai Kinerja
            </Text>
            <Text as="li">Mulai tahap Pelaksanaan</Text>
          </ol>

          <Button
            as="a"
            variant="ghost"
            size="lg"
            href={PANDUAN_TAHAPAN_PERENCANAAN_KINERJA_URL}
            target="_blank"
            rel="noreferrer"
          >
            <Icon fontSize="sm" color="informational">
              help
            </Icon>
            Tentang Perencanaan Kinerja
          </Button>
        </SimpleCard>

        <div className="flex flex-col gap-5">
          {apiStatus === 'success' && isNeedRevision && (
            <SimpleCard
              data-testid="card-need-revision"
              className="flex flex-col gap-2 p-5 w-[632px]"
            >
              <Text variant="heading-sm">
                Lakukan revisi sesuai arahan dari{' '}
                {isNeedRevisionByTK ? 'Tim Kinerja' : 'Pejabat Penilai Kinerja'}
                :
              </Text>

              <Text>{perencanaan?.catatanRevisiPerencanaan}</Text>

              <div className="flex items-center gap-3">
                <Icon color="critical">info</Icon>

                <Text color="critical">Anda perlu ajukan ulang.</Text>
              </div>
            </SimpleCard>
          )}
          <SimpleCard className="flex flex-col gap-2 p-3 w-[632px] h-fit">
            {apiStatus === 'success' && (
              <>
                {isPerencanaanV2 && (
                  <PlanningCard
                    data-testid="tugas-pokok-card"
                    imageSrc="/illustrations/img-learning-instruction.png"
                    title="Tugas Pokok"
                    state={perencanaan.tugasPokokStatus}
                  >
                    <TugasPokokCTA state={perencanaan.tugasPokokStatus} />
                  </PlanningCard>
                )}
                <PlanningCard
                  useV2={isPerencanaanV2}
                  data-testid="praktik-kinerja-card"
                  imageSrc="/illustrations/img-interactive-activity.png"
                  title="Praktik Kinerja"
                  isDraft={hasPraktikKinerjaDraft()}
                  state={perencanaan.praktikKinerjaStatus}
                  href={`${PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH}/perencanaan/praktik-kinerja`}
                  onCtaClick={() => {
                    trackEvent(
                      'perencanaan pegawai',
                      'create draft perencanaan',
                      {
                        aspect: 'praktik kinerja',
                      }
                    )
                  }}
                  customButtonState={
                    isPerencanaanV2
                      ? { SIAP_DIAJUKAN: { color: 'white', text: 'Cek' } }
                      : undefined
                  }
                />
                <PlanningCard
                  useV2={isPerencanaanV2}
                  imageSrc="/illustrations/img-reflection-documents-magnifying-glass.png"
                  title="Perilaku Kerja"
                  isDraft={hasPerilakuKerjaDraft()}
                  state={perencanaan.perilakuKerjaStatus}
                  href={`${PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH}/perencanaan/perilaku-kerja`}
                  onCtaClick={() => {
                    trackEvent(
                      'perencanaan pegawai',
                      'create draft perencanaan',
                      {
                        aspect: 'perilaku kerja',
                      }
                    )
                  }}
                />
                <PlanningCard
                  useV2={isPerencanaanV2}
                  imageSrc="/illustrations/img-flow-process.png"
                  title="Pengembangan Kompetensi"
                  isDraft={hasPengembanganKompetensiDraft()}
                  state={perencanaan.pengembanganKompetensiStatus}
                  href={`${PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH}/perencanaan/pengembangan-kompetensi`}
                  onCtaClick={() => {
                    trackEvent(
                      'perencanaan pegawai',
                      'create draft perencanaan',
                      {
                        aspect: 'pengembangan kompetensi',
                      }
                    )
                  }}
                />
                <PlanningCard
                  imageSrc="/illustrations/img-book-rapor-graphic-doc.png"
                  title="Pelaksanaan Tugas"
                >
                  <Button
                    size="md"
                    color="white"
                    onClick={() => setOpenDialog('pelaksanaan-tugas')}
                  >
                    {isPerencanaanV2 ? 'Cek' : 'Selengkapnya'}
                  </Button>
                </PlanningCard>
              </>
            )}
          </SimpleCard>

          {isRangkumanEnabled && (
            <SimpleCard className="px-9 py-6 w-[632px] h-fit">
              <div className="flex items-center justify-between flex-1 gap-1">
                <div className="flex flex-col gap-3">
                  <Text variant="heading-sm">Rangkuman Perencanaan</Text>
                </div>

                <div className="ml-auto">
                  <NextLink
                    href={`${PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH}/perencanaan/rangkuman`}
                  >
                    <Button size="md" color="white" className="w-32">
                      Cek
                    </Button>
                  </NextLink>
                </div>
              </div>
            </SimpleCard>
          )}
        </div>
      </div>

      <DialogMulaiPelaksanaanConfirmation
        isOpen={openDialog === 'mulai-pelaksanaan'}
        onClose={() => setOpenDialog(null)}
      />

      <DialogPelaksanaanTugas
        isOpen={openDialog === 'pelaksanaan-tugas'}
        onClose={() => setOpenDialog(null)}
      />
    </div>
  )
}

export default PSPerencanaanBerandaPage
